import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import crmApi, { money, STAGE_META, STAGE_ORDER, CURRENCIES, DEAL_SOURCES, LOST_REASONS } from '../../utils/crmApi';
import useLiveRefresh from '../../utils/useLiveRefresh';

export default function CrmPipeline() {
  const [deals, setDeals] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(blankDeal());
  const navigate = useNavigate();

  function blankDeal() {
    return { title: '', account_id: '', owner_id: '', value: '', currency: 'OMR', probability: 10, stage: 'lead', source: '', expected_close_date: '', description: '', visibility: 'private', member_ids: [], items: [] };
  }

  const nowY = new Date().getFullYear();
  const [year, setYear] = useState('all');

  function load(silent) {
    if (!silent) setLoading(true);
    const yq = year === 'all' ? '' : `?year=${year}`;
    Promise.all([crmApi.get(`/deals${yq}`), crmApi.get('/accounts')])
      .then(([d, a]) => { setDeals(d); setAccounts(a); })
      .finally(() => { if (!silent) setLoading(false); });
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);
  useLiveRefresh(() => load(true));
  useEffect(() => { crmApi.get('/users').then(u => setUsers(Array.isArray(u) ? u : [])).catch(() => {}); }, []);
  useEffect(() => { crmApi.get('/products').then(p => setProducts(Array.isArray(p) ? p : [])).catch(() => {}); }, []);

  async function createDeal() {
    if (!form.title.trim()) return;
    await crmApi.post('/deals', {
      ...form,
      value: parseFloat(form.value) || 0,
      owner_id: form.owner_id || undefined,
      items: (form.items || []).filter(it => it.product_id || it.name).map(it => ({
        product_id: it.product_id || null, name: it.name, quantity: parseFloat(it.quantity) || 1,
        unit_price: parseFloat(it.unit_price) || 0, cost_price: parseFloat(it.cost_price) || 0,
        currency: it.currency, billing_type: it.billing_type,
      })),
    });
    setShowCreate(false); setForm(blankDeal()); load();
  }

  function addDealItem() { setForm(f => ({ ...f, items: [...f.items, { product_id: '', name: '', quantity: 1, unit_price: 0, cost_price: 0, currency: f.currency, billing_type: 'one_time' }] })); }
  function removeDealItem(i) { setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) })); }
  function setDealItem(i, patch) { setForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) })); }
  function pickDealProduct(i, pid) {
    const p = products.find(x => x.id === pid);
    if (p) setDealItem(i, { product_id: p.id, name: p.name, unit_price: Number(p.unit_price), cost_price: Number(p.cost_price) || 0, currency: p.currency, billing_type: p.billing_type });
    else setDealItem(i, { product_id: '' });
  }

  const [pendingMove, setPendingMove] = useState(null); // { deal, newStage }
  const [moveLostReason, setMoveLostReason] = useState('');
  const [moveLostCat, setMoveLostCat] = useState('');
  const [moveNote, setMoveNote] = useState('');
  const [moveErr, setMoveErr] = useState(null);

  function requestMove(deal, newStage) {
    if (deal.stage === newStage) return;
    if (deal.closed) { setMoveErr('This deal is closed. Only a CRM admin can move it.'); setTimeout(() => setMoveErr(null), 4000); return; }
    setMoveLostReason(''); setMoveNote(''); setMoveLostCat('');
    setPendingMove({ deal, newStage });
  }
  async function confirmMove() {
    const { deal, newStage } = pendingMove;
    let lostReason;
    if (newStage === 'lost') {
      if (!moveLostCat) { setMoveErr('Please choose a reason for the loss.'); setTimeout(() => setMoveErr(null), 4000); return; }
      lostReason = moveLostCat === 'Other' ? (moveLostReason.trim() || 'Other') : moveLostCat;
    }
    try {
      await crmApi.post(`/deals/${deal.id}/stage`, { stage: newStage, lost_reason: lostReason, note: moveNote || undefined });
      setPendingMove(null); load();
    } catch (e) {
      setMoveErr(e?.error || 'Could not move the deal.'); setPendingMove(null); setTimeout(() => setMoveErr(null), 4000);
    }
  }

  const columns = STAGE_ORDER;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: '800' }}>Pipeline</h1>
          <p style={{ fontSize: '13px', color: 'var(--muted)' }}>Track deals from lead to close. Drag the stage selector on a card to move it.</p>
        </div>
        <select className="select" style={{ width: 120, marginLeft: 'auto', marginRight: 10 }} value={year} onChange={e => setYear(e.target.value)}>
          <option value="all">All years</option>
          {Array.from({ length: (2035 - 2022) + 1 }, (_, i) => 2035 - i).map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ New Deal</button>
      </div>

      {loading ? <div style={{ color: 'var(--muted)' }}>Loading…</div> : (
        <div style={{ display: 'flex', gap: '12px', paddingBottom: '10px' }}>
          {columns.map(stage => {
            const meta = STAGE_META[stage];
            const items = deals.filter(d => d.stage === stage);
            const total = items.reduce((s, d) => s + Number(d.value || 0), 0);
            return (
              <div key={stage} style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '8px', padding: '0 2px' }}>
                  <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: meta.color }} />
                  <span style={{ fontSize: '12px', fontWeight: '800', color: 'var(--text)' }}>{meta.label}</span>
                  <span style={{ fontSize: '11px', color: 'var(--muted)' }}>({items.length})</span>
                  <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: '700', color: meta.color }}>{money(total).replace('OMR ', '')}</span>
                </div>
                <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius-lg)', padding: '8px', minHeight: '120px' }}>
                  {items.length === 0 && <div style={{ fontSize: '11px', color: 'var(--muted)', textAlign: 'center', padding: '14px 0' }}>—</div>}
                  {items.map(d => (
                    <div key={d.id} className="card" style={{ padding: '11px', marginBottom: '8px', cursor: 'pointer' }}
                      onClick={() => navigate(`/crm/deals/${d.id}`)}>
                      <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        {(d.won_from_so === 1 || d.won_from_so === true) && <span title="Won via confirmed service order" style={{ fontSize: '11px' }}>📄✓</span>}
                        <span>{d.title}</span>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '7px' }}>{d.account_name || 'No account'}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '10px', color: 'var(--muted)' }}>Estimate</span>
                        <span style={{ fontSize: '12px', fontWeight: '800', color: '#0D9488' }}>{money(d.value, d.currency)}</span>
                      </div>
                      {d.items_gross > 0 && (() => {
                        const est = Number(d.value || 0);
                        const diff = Math.round((Number(d.items_gross) - est) * 1000) / 1000;
                        const pctv = est > 0 ? Math.round((Number(d.items_gross) - est) / est * 100) : (Number(d.items_gross) > 0 ? 100 : 0);
                        const dc = diff > 0 ? '#16A34A' : diff < 0 ? '#DC2626' : 'var(--muted)';
                        return (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                            <span style={{ fontSize: '10px', color: 'var(--muted)' }}>Sales (items)</span>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#2563EB' }}>{money(d.items_gross, d.currency)} <span style={{ color: dc, fontSize: '9px' }}>{diff >= 0 ? '+' : ''}{pctv}%</span></span>
                          </div>
                        );
                      })()}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                        <span style={{ fontSize: '10px', color: 'var(--muted)' }}>{d.probability}%</span>
                      </div>
                      <select value={d.stage} onClick={e => e.stopPropagation()} onChange={e => { e.stopPropagation(); requestMove(d, e.target.value); }}
                        style={{ marginTop: '8px', width: '100%', fontSize: '11px', padding: '4px 6px', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer' }}>
                        {STAGE_ORDER.map(s => <option key={s} value={s}>{STAGE_META[s].label}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowCreate(false); }}>
          <div className="modal" style={{ width: '480px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '16px' }}>New Deal</h3>
            <div className="form-group">
              <label className="form-label">Deal Title *</label>
              <input className="input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Starlink terminals — Acme Ltd" />
            </div>
            <div className="form-group">
              <label className="form-label">Account</label>
              <select className="select" value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })}>
                <option value="">— None —</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Assigned to <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(salesperson who owns this deal)</span></label>
              <select className="select" value={form.owner_id} onChange={e => setForm({ ...form, owner_id: e.target.value })}>
                <option value="">Me (creator)</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Expected Value</label>
                <input className="input" type="number" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder="0.000" />
              </div>
              <div className="form-group" style={{ width: '120px' }}>
                <label className="form-label">Currency</label>
                <select className="select" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
                  {CURRENCIES.map(cur => <option key={cur} value={cur}>{cur}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Stage</label>
                <select className="select" value={form.stage} onChange={e => setForm({ ...form, stage: e.target.value })}>
                  {STAGE_ORDER.map(s => <option key={s} value={s}>{STAGE_META[s].label}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ width: '120px' }}>
                <label className="form-label">Probability %</label>
                <input className="input" type="number" value={form.probability} onChange={e => setForm({ ...form, probability: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Source</label>
                <select className="select" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}>
                  <option value="">— Select source —</option>
                  {DEAL_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Expected Close <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(DD/MM/YYYY)</span></label>
                <input className="input" type="date" value={form.expected_close_date} onChange={e => setForm({ ...form, expected_close_date: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notes / Description</label>
              <textarea className="input" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Background, requirements, anything useful for this deal…" />
            </div>

            {/* Optional product line items */}
            <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius)', padding: '12px', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--muted2)' }}>Products (optional) — used to build the service order when this deal is won</div>
                <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={addDealItem}>+ Add</button>
              </div>
              {(form.items || []).length === 0 && <div style={{ fontSize: '11px', color: 'var(--muted)' }}>No products added. You can add them later too.</div>}
              {(form.items || []).map((it, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 0.7fr 1fr 34px', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                  <select className="select" value={it.product_id} onChange={e => pickDealProduct(i, e.target.value)}>
                    <option value="">Select product…</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <input className="input" type="number" value={it.quantity} onChange={e => setDealItem(i, { quantity: e.target.value })} placeholder="Qty" title="Quantity" />
                  <div style={{ fontSize: '12px', fontWeight: '600', textAlign: 'right', paddingRight: '4px' }}>{it.currency || form.currency} {((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0)).toFixed(3)}</div>
                  <button onClick={() => removeDealItem(i)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '5px', cursor: 'pointer', color: '#DC2626', height: '34px' }}>✕</button>
                </div>
              ))}
            </div>

            {/* Privacy */}
            <div className="form-group">
              <label className="form-label">Privacy</label>
              <select className="select" value={form.visibility} onChange={e => setForm({ ...form, visibility: e.target.value })}>
                <option value="private">Private — only assigned people, the owner and admins</option>
                <option value="public">Public — everyone in the CRM can see it</option>
              </select>
            </div>
            {form.visibility === 'private' && (
              <div className="form-group">
                <label className="form-label">Who can access this deal <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(you & admins always can)</span></label>
                <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px' }}>
                  {users.map(u => (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', padding: '4px 6px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.member_ids.includes(u.id)}
                        onChange={e => setForm(f => ({ ...f, member_ids: e.target.checked ? [...f.member_ids, u.id] : f.member_ids.filter(x => x !== u.id) }))} />
                      {u.full_name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
              <button className="btn" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={createDeal}>Create Deal</button>
            </div>
          </div>
        </div>
      )}

      {/* Stage move confirmation */}
      {pendingMove && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setPendingMove(null); }}>
          <div className="modal" style={{ width: '420px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '10px' }}>
              {pendingMove.newStage === 'won' ? 'Mark deal as Won?' : pendingMove.newStage === 'lost' ? 'Mark deal as Lost?' : 'Move deal stage?'}
            </h3>
            <p style={{ fontSize: '13px', color: 'var(--muted2)', lineHeight: 1.6, marginBottom: '14px' }}>
              You are moving <strong>{pendingMove.deal.title}</strong> from <strong>{STAGE_META[pendingMove.deal.stage]?.label}</strong> to <strong>{STAGE_META[pendingMove.newStage]?.label}</strong>.
              {(pendingMove.newStage === 'won' || pendingMove.newStage === 'lost') && ' Once closed, only a CRM admin can move it again.'}
              {pendingMove.newStage === 'won' && ' A draft service order will be created automatically.'}
            </p>
            {pendingMove.newStage === 'lost' && (
              <div className="form-group">
                <label className="form-label">Reason for loss <span style={{ color: '#DC2626' }}>*</span></label>
                <select className="select" value={moveLostCat} onChange={e => setMoveLostCat(e.target.value)}>
                  <option value="">Select a reason…</option>
                  {LOST_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                {moveLostCat === 'Other' && (
                  <textarea className="input" rows={2} value={moveLostReason} onChange={e => setMoveLostReason(e.target.value)} placeholder="Describe the reason" style={{ marginTop: '8px' }} />
                )}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Note <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(optional — added to deal history)</span></label>
              <textarea className="input" rows={2} value={moveNote} onChange={e => setMoveNote(e.target.value)} placeholder="Add a comment about this move…" />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
              <button className="btn" onClick={() => setPendingMove(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmMove}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {moveErr && (
        <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', background: '#DC2626', color: '#fff', padding: '10px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', zIndex: 1000 }}>{moveErr}</div>
      )}
    </div>
  );
}
